/**
 * Paso 6 de la «Primera travesía», «Rumbo también se planifica» (decidido
 * por Vadik el 1-nov-2026): la persona crea la categoría Rumbo y aparta el
 * precio de un mes en el mes en que acaba su prueba. Funciones puras: quién
 * lo ve, qué fecha y mes tocan y qué líneas lleva la nota de la categoría.
 */
import type { EstadoPlan } from '@actual-app/core/server/rumbo/plan-app';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { importeDePlantilla } from '#components/mobile/budget/objetivos';

/** Días de prueba si el servidor no dice la fecha (solo el admin, para probarlo). */
export const DIAS_PRUEBA = 45;

/**
 * Cómo se ve el paso:
 * - `completo`: está en su prueba gratis (o es el admin, para probarlo).
 * - `beta`: tripulación de la beta; una línea de Gavi y «Seguir».
 * - `no`: ya paga, va como pareja de alguien que paga, no hay servidor o el
 *   servidor es de antes y no manda el precio. El paso se salta solo.
 * - `cargando`: aún no se sabe.
 */
export type VistaRumbo = 'cargando' | 'completo' | 'beta' | 'no';

export function vistaRumbo({
  plan,
  cargando,
  esAdmin,
}: {
  plan: EstadoPlan | null;
  cargando: boolean;
  esAdmin: boolean;
}): VistaRumbo {
  if (cargando) {
    return 'cargando';
  }
  if (!plan?.precios) {
    return 'no';
  }
  if (esAdmin) {
    return 'completo';
  }
  if (plan.aBordo) {
    return 'no';
  }
  if (plan.estado === 'prueba' && plan.gratisHasta != null) {
    return 'completo';
  }
  return plan.tramo === 'beta' ? 'beta' : 'no';
}

export type DatosRumbo = {
  /** Día en que acaba la prueba, `YYYY-MM-DD` en hora local. */
  fin: string;
  /** Día del mes del cobro (el del fin de la prueba). */
  dia: number;
  /**
   * Mes donde se aparta (`YYYY-MM`): el del fin de la prueba y, si ese ya es
   * el mes en curso, el siguiente, para que la lección sea siempre un mes
   * que aún no ha llegado.
   */
  mes: string;
  /** Precio de lista mensual (céntimos). */
  precio: IntegerAmount;
};

/**
 * Fecha, mes e importe del paso. `gratisHasta` en segundos (servidor); sin
 * ella (el admin), hoy + 45 días.
 */
export function datosRumbo(
  gratisHasta: number | null,
  precio: IntegerAmount,
  hoy: string,
): DatosRumbo {
  const fin =
    gratisHasta != null
      ? monthUtils.dayFromDate(new Date(gratisHasta * 1000))
      : monthUtils.addDays(hoy, DIAS_PRUEBA);
  const mesHoy = monthUtils.monthFromDate(hoy);
  const mesFin = monthUtils.monthFromDate(fin);
  return {
    fin,
    dia: monthUtils.getDay(fin),
    mes: mesFin > mesHoy ? mesFin : monthUtils.addMonths(mesHoy, 1),
    precio,
  };
}

/**
 * Nota de la categoría: pago fijo de cada mes desde el fin de la prueba
 * (antes, la plantilla no pide nada) y el día del cobro. Se lee como
 * objetivo mensual con su día en la ficha. Ojo: Actual no entiende «repeat
 * every month» (sí «every 1 months»).
 */
export function lineasRumbo(datos: DatosRumbo): string[] {
  return [
    `#template ${importeDePlantilla(datos.precio)} repeat every 1 months starting ${datos.fin}`,
    `#objetivo día ${datos.dia}`,
  ];
}

/** Hay dinero sin asignar para apartar el precio entero. */
export function hayDineroParaRumbo(
  listoParaAsignar: IntegerAmount | null,
  precio: IntegerAmount,
): boolean {
  return listoParaAsignar != null && listoParaAsignar >= precio;
}
